import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Camera, Mic2, Film, Download, CupSoda, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FILMED_VIDEO } from "./homeData";

const FEATURES = [
  { icon: Camera, title: "Shot on Nikon Z5 + GoPro", sub: "Multi-angle video made for Reels, TikTok & Shorts" },
  { icon: Mic2, title: "Full set audio recorded", sub: "High-quality recording straight off the DJM-A9" },
  { icon: Download, title: "Raw clips delivered", sub: "Every angle, yours to cut and post" },
  { icon: CupSoda, title: "2 hrs room hire + drinks", sub: "Free water & soft drinks included" },
  { icon: Headphones, title: "Sennheiser HD 25s", sub: "Plus fast WiFi, free to use" },
];

export const FilmedVideo = ({ className = "", testId = "filmed-video" }) => (
  <video src={FILMED_VIDEO} autoPlay muted loop playsInline preload="metadata" className={`w-full h-full object-cover ${className}`} data-testid={testId} />
);

const PhoneFrame = () => (
  <motion.div
    initial={{ opacity: 0, y: 40, rotate: -4 }}
    whileInView={{ opacity: 1, y: 0, rotate: -3 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
    className="relative mx-auto w-[260px] sm:w-[300px] aspect-[9/16] rounded-[2.5rem] p-2 bg-gradient-to-b from-white/20 to-white/5 shadow-[0_30px_80px_rgba(139,92,246,0.35)]"
  >
    <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-black">
      <FilmedVideo />
      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold text-white tracking-widest">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> REC
      </span>
    </div>
    <span className="absolute -right-6 top-16 rotate-6 px-3 py-1.5 rounded-full bg-yellow-400 text-black text-xs font-bold shadow-lg">Ready for socials</span>
  </motion.div>
);

export const FilmedSetsSection = () => (
  <section className="relative py-24 md:py-32 bg-dream-surface overflow-hidden" data-testid="filmed-sets-section">
    <div className="absolute -left-40 top-1/3 w-[500px] h-[500px] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[auto_1fr] gap-14 lg:gap-24 items-center relative">
      <PhoneFrame />
      <div>
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-fuchsia-400 mb-4 flex items-center gap-2"><Film className="w-4 h-4" /> New · Content Session</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.02]">
          We record your sets <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-cyan-300">for your socials.</span>
        </h2>
        <p className="text-white/60 text-base md:text-lg mt-5 max-w-xl">Dream Maker Sessions + Filmed — you play, we capture. Walk out with content that actually looks and sounds like a club.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-8">
          {FEATURES.map(({ icon: Icon, title, sub }) => (
            <div key={title} className="flex gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/10">
              <Icon className="w-5 h-5 text-fuchsia-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-semibold">{title}</p>
                <p className="text-white/45 text-xs mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5 mt-10">
          <div>
            <p className="font-heading text-5xl font-extrabold text-white" data-testid="filmed-price">$125</p>
            <p className="text-white/50 text-sm">Flat rate · 2 hours · or 4 × $31.25</p>
          </div>
          <Link to="/booking?service=package_dj_filmed">
            <Button className="group bg-gradient-to-r from-fuchsia-500 to-dream-violet hover:opacity-90 text-white rounded-full px-8 py-7 text-base font-bold" data-testid="filmed-book-btn">
              Book a Filmed Session <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);
